import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate } from 'react-router-dom';
import { MapViewer } from './components/MapViewer';
import { Footer } from './components/Footer';
import { SetPasswordScreen } from './components/EmailVerificationScreen';
import { AcceptInvitationScreen } from './components/AcceptInvitationScreen';
import { PrivacyPolicy } from './components/PrivacyPolicy';
import { TermsOfService } from './components/TermsOfService';
import { StatusModal } from './components/StatusModal';
import { ClaimBundlePage } from './pages/ClaimBundlePage';
import { useEffect, useRef, useState } from 'react';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { UsagePurposePrompt } from './components/UsagePurposePrompt';
import { FirstActionMapCard } from './components/onboarding/FirstActionCard';
import { ProjectCreationProvider } from './contexts/ProjectCreationContext';
import { BillingProvider } from './contexts/BillingContext';
import { projectService } from './services/projectService';
import { UserProjectsProvider } from './contexts/UserProjectsContext';
import { useEventSubscribe } from './events';
import { eventBus } from './events';
import './styles.css';
const App = () => {
    const [errorBanner, setErrorBanner] = useState(null);
    const [statusMessage, setStatusMessage] = useState(null);
    // Initialize KMZ download listeners once on mount
    React.useEffect(() => {
        projectService.initDownloadListeners();
    }, []);
    // Listen for error banner events via event bus
    useEventSubscribe('notification:error', ({ message }) => {
        setErrorBanner(message);
        setTimeout(() => setErrorBanner(null), 10000);
    }, []);
    // Listen for status message events via event bus
    useEventSubscribe('notification:status', ({ message }) => {
        setStatusMessage(message);
        setTimeout(() => setStatusMessage(null), 5000);
    }, []);
    return (_jsxs(AuthProvider, { children: [_jsx(UserProjectsProvider, { children: _jsx(BillingProvider, { children: _jsx(ProjectCreationProvider, { children: _jsxs(Router, { basename: REACT_APP_BASE_PATH, children: [errorBanner && (_jsx("div", { className: "fixed top-0 left-0 right-0 z-50 p-4 bg-red-50 border-b border-red-200", children: _jsxs("div", { className: "flex items-center justify-between max-w-7xl mx-auto", children: [_jsx("div", { className: "flex items-center", children: _jsx("p", { className: "text-red-600 text-sm font-medium", children: errorBanner }) }), _jsx("button", { onClick: () => setErrorBanner(null), className: "text-red-400 hover:text-red-600 transition-colors", children: _jsx("svg", { className: "w-5 h-5", fill: "currentColor", viewBox: "0 0 20 20", children: _jsx("path", { fillRule: "evenodd", d: "M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z", clipRule: "evenodd" }) }) })] }) })), _jsxs(Routes, { children: [_jsx(Route, { path: "/verify-email", element: _jsx(SetPasswordScreen, {}) }), _jsx(Route, { path: "/reset-password", element: _jsx(SetPasswordScreen, {}) }), _jsx(Route, { path: "/accept-invitation", element: _jsx(AcceptInvitationScreen, {}) }), _jsx(Route, { path: "/privacy-policy", element: _jsx(PrivacyPolicy, {}) }), _jsx(Route, { path: "/terms-of-service", element: _jsx(TermsOfService, {}) }), _jsx(Route, { path: "/claim-bundle", element: _jsx(ClaimBundlePage, {}) }), _jsx(Route, { path: "/auth/idp/success", element: _jsx(IdpSuccessHandler, {}) }), _jsx(Route, { path: "/auth/idp/fail", element: _jsx(IdpFailHandler, {}) }), _jsx(Route, { path: "/", element: _jsx(HomeRoute, { errorBanner: errorBanner }) })] })] }) }) }) }), _jsx(StatusModal, { isOpen: !!statusMessage, message: statusMessage || '', onClose: () => setStatusMessage(null) })] }));
};
export default App;
// Inline lightweight handlers to avoid new files
import { useSearchParams } from 'react-router-dom';
import { authService } from './services/authService';
import { parseReferralCode } from './utils/referralCode';
import { GoogleLoginErrorModal } from './components/GoogleLoginErrorModal';
import { EntryGate } from './components/EntryGate';
// Entry auth wall: the app is only reachable with a full account. Unauthenticated
// visitors see the EntryGate instead of the map viewer.
const HomeRoute = ({ errorBanner }) => {
    const { isAuthenticated, isLoading } = useAuth();
    if (isLoading) {
        return _jsx("div", { className: "w-full h-screen", style: { backgroundColor: 'var(--charcoal)' } });
    }
    if (!isAuthenticated) {
        return _jsx(EntryGate, {});
    }
    return (_jsxs("div", { className: "w-full h-screen flex flex-col", style: { backgroundColor: 'var(--charcoal)', marginTop: errorBanner ? '60px' : '0' }, children: [_jsx("div", { className: "flex-1 overflow-hidden", children: _jsx(MapViewer, {}) }), _jsx(Footer, {}), _jsx(FirstActionMapCard, {}), _jsx(UsagePurposePrompt, {})] }));
};
const IdpSuccessHandler = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { storeTokens } = useAuth();
    const handledRef = useRef(false);
    useEffect(() => {
        if (handledRef.current) {
            return;
        }
        handledRef.current = true;
        // Be liberal in what we accept: ZITADEL may use different casings and may send in hash fragment
        const hash = window.location.hash ? (window.location.hash.startsWith('#') ? window.location.hash.slice(1) : window.location.hash) : '';
        const hashParams = hash ? new URLSearchParams(hash) : null;
        const intentId = searchParams.get('idpIntentId') ||
            searchParams.get('idpIntentID') ||
            searchParams.get('idp_intent_id') ||
            searchParams.get('intentId') ||
            searchParams.get('intentID') ||
            searchParams.get('id') ||
            ((hashParams === null || hashParams === void 0 ? void 0 : hashParams.get('idpIntentId')) || (hashParams === null || hashParams === void 0 ? void 0 : hashParams.get('idpIntentID')) || (hashParams === null || hashParams === void 0 ? void 0 : hashParams.get('idp_intent_id')) || (hashParams === null || hashParams === void 0 ? void 0 : hashParams.get('intentId')) || (hashParams === null || hashParams === void 0 ? void 0 : hashParams.get('intentID')) || (hashParams === null || hashParams === void 0 ? void 0 : hashParams.get('id')) || '') ||
            '';
        const intentToken = searchParams.get('idpIntentToken') ||
            searchParams.get('idp_intent_token') ||
            searchParams.get('token') ||
            ((hashParams === null || hashParams === void 0 ? void 0 : hashParams.get('idpIntentToken')) || (hashParams === null || hashParams === void 0 ? void 0 : hashParams.get('idp_intent_token')) || (hashParams === null || hashParams === void 0 ? void 0 : hashParams.get('token')) || '') ||
            '';
        if (!intentId || !intentToken) {
            navigate('/');
            return;
        }
        (async () => {
            try {
                // The server added the partner code (if any) to this success URL.
                const tokens = await authService.completeExternalLogin(intentId, intentToken, parseReferralCode(searchParams));
                storeTokens(tokens.sessionId, tokens.sessionToken);
                // Check if this was an invitation acceptance flow
                const invitationUserId = sessionStorage.getItem('invitation_userId');
                const invitationCode = sessionStorage.getItem('invitation_code');
                const invitationCsrf = sessionStorage.getItem('invitation_csrf');
                if (invitationUserId && invitationCode && invitationCsrf) {
                    // Clear invitation data from sessionStorage
                    sessionStorage.removeItem('invitation_userId');
                    sessionStorage.removeItem('invitation_code');
                    sessionStorage.removeItem('invitation_csrf');
                    // Complete invitation acceptance using existing verify method
                    try {
                        await authService.verify({
                            userId: invitationUserId,
                            code: invitationCode,
                            password: '', // Empty password for Google OAuth flow
                            csrfToken: invitationCsrf
                        });
                    }
                    catch (inviteError) {
                        console.error('Failed to complete invitation acceptance after Google OAuth:', inviteError);
                        // Show error but don't fail the login - user is already authenticated
                        eventBus.emit('notification:error', { message: 'Account created successfully, but invitation processing failed. Please contact support if needed.' });
                    }
                }
                navigate('/');
            }
            catch (e) {
                console.error('Failed to complete external login', e);
                // Handle domain restriction errors by showing error banner
                if (e instanceof Error && e.message === 'EMAIL_DOMAIN_NOT_ALLOWED') {
                    eventBus.emit('notification:error', { message: 'Email domain not authorized for login. Please use an approved email address or contact support.' });
                    navigate('/');
                    return;
                }
                // For other external login errors, show a generic error banner
                eventBus.emit('notification:error', { message: 'Login failed. Please try again or contact support if the problem persists.' });
                navigate('/');
            }
        })();
    }, [searchParams]);
    return null;
};
const IdpFailHandler = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [showErrorModal, setShowErrorModal] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const handledRef = useRef(false);
    useEffect(() => {
        if (handledRef.current) {
            return;
        }
        handledRef.current = true;
        // Extract error details from URL parameters
        const error = searchParams.get('error') || 'Unknown authentication error';
        const errorDescription = searchParams.get('error_description') || '';
        const id = searchParams.get('id') || '';
        // Parse the error message to provide user-friendly feedback
        let userMessage = 'Authentication failed. Please try again.';
        if (error.includes('issuedAt') && error.includes('future')) {
            userMessage = 'Authentication failed due to a timing issue. This is usually temporary - please try logging in again in a few moments.';
        }
        else if (error.includes('domain') && error.includes('authorized')) {
            userMessage = 'Email domain not authorized for login. Please use an approved email address or contact support.';
        }
        else if (errorDescription) {
            userMessage = `Authentication failed: ${errorDescription}`;
        }
        else if (error !== 'Unknown authentication error') {
            userMessage = `Authentication failed: ${error}`;
        }
        setErrorMessage(userMessage);
        setShowErrorModal(true);
    }, [searchParams]);
    const handleCloseModal = () => {
        setShowErrorModal(false);
        navigate('/');
    };
    return (_jsx(GoogleLoginErrorModal, { isOpen: showErrorModal, errorMessage: errorMessage, onClose: handleCloseModal }));
};
